/**
 * Career DNA™ context for presentation surfaces.
 *
 * Reads the sealed student payload (`learner_reports`, audience = student) and
 * projects the three things the shell needs: primary archetype, age band and
 * the top matches. It computes nothing — if the payload has no archetype, this
 * returns null and the UI says so rather than guessing.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { parseBand, DEFAULT_BAND, type Band } from "@/features/assessment-v2/band";
import { asArchetype } from "@/features/roadmap/roadmaps";
import type { Archetype } from "@/features/assessment-v2/archetypes";
import { isPreviewMode, previewStudentPayload } from "@/lib/previewMode";
import { localDnaBand, readLocalDna, type LocalDnaSnapshot } from "@/features/assessment-v2/localDna";
import { backfillDnaSnapshot } from "@/features/assessment-v2/snapshot";

export interface CareerDna {
  archetypePrimary: Archetype | null;
  archetypeSecondary: Archetype | null;
  band: Band;
  displayName: string | null;
  topIntelligences: Array<{ name: string; value: number | null }>;
  matches: Array<{ id: string; name: string; cluster: string; tier: string | null; explanation: string | null }>;
  /** True when this came from the dev-only preview door. */
  isPreview: boolean;
  /**
   * True when this is the on-device result of a just-finished run and the
   * sealed report has not landed yet. The shell says so rather than
   * pretending the numbers are canonical.
   */
  isLocalPreview?: boolean;
  /** When the local snapshot was written, for the "just finished" copy. */
  localSavedAt?: string | null;
}


function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function project(payload: Record<string, unknown>, isPreview: boolean): CareerDna {
  const identity = rec(payload.identity);
  const archetype = rec(payload.archetype);
  const learner = rec(payload.learner);
  const dna = rec(payload.career_dna);
  const top = Array.isArray(dna.top_intelligences) ? dna.top_intelligences : [];
  const recs = Array.isArray(payload.recommendations) ? payload.recommendations : [];

  return {
    archetypePrimary: asArchetype(
      str(archetype.primary) ?? str(identity.archetype_primary) ?? str(payload.archetype_primary),
    ),
    archetypeSecondary: asArchetype(
      str(archetype.secondary) ?? str(identity.archetype_secondary) ?? str(payload.archetype_secondary),
    ),
    band: parseBand(str(learner.age_group)) ?? DEFAULT_BAND,
    displayName: str(learner.display_name),
    topIntelligences: top.slice(0, 3).map((r) => {
      const row = rec(r);
      return {
        name: str(row.intelligence_name) ?? "",
        value: typeof row.value === "number" ? row.value : null,
      };
    }),
    matches: recs.slice(0, 4).map((r) => {
      const row = rec(r);
      return {
        id: str(row.career_id) ?? "",
        name: str(row.career_name) ?? "",
        cluster: str(row.career_cluster) ?? "",
        tier: str(row.tier),
        explanation: str(row.explanation),
      };
    }).filter((m) => m.name !== ""),
    isPreview,
  };
}

/**
 * Project the on-device snapshot the v2 result page wrote when the run
 * finished. Used only when no sealed report exists yet, and always flagged
 * as a preview so no surface presents it as canonical.
 */
function projectLocal(snap: LocalDnaSnapshot): CareerDna {
  return {
    archetypePrimary: asArchetype(snap.archetypePrimary),
    archetypeSecondary: asArchetype(snap.archetypeSecondary),
    band: localDnaBand(snap),
    displayName: null,
    topIntelligences: [],
    matches: snap.matches.filter((m) => m.name),
    isPreview: false,
    isLocalPreview: true,
    localSavedAt: snap.savedAt,
  };
}

export function useCareerDna() {
  const { user } = useAuth();
  const preview = isPreviewMode();

  return useQuery({
    // The local snapshot changes the moment a run finishes, so it is part of
    // the key — otherwise a cached "no DNA" answer outlives the assessment.
    queryKey: ["career-dna", user?.id ?? (preview ? "preview" : "anon"), readLocalDna()?.savedAt ?? "none"],
    enabled: Boolean(user?.id) || preview || Boolean(readLocalDna()),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CareerDna | null> => {
      const local = readLocalDna();
      if (!user?.id) {
        if (preview) return project(previewStudentPayload(), true);
        return local ? projectLocal(local) : null;
      }
      const { data } = await supabase
        .from("learner_reports")
        .select("payload")
        .eq("learner_id", user.id)
        .eq("audience", "student")
        .order("generated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      // A sealed report exists but carries no archetype and no matches — the
      // pipeline ran ahead of the canon it needs. An empty authority is not an
      // authority: fall through to the run the learner actually completed.
      const sealed = data?.payload ? project(rec(data.payload), false) : null;
      const sealedHasContent = Boolean(
        sealed && (sealed.archetypePrimary || sealed.matches.length > 0 || sealed.topIntelligences.length > 0),
      );

      if (!sealedHasContent) {
        if (local) return projectLocal(local);
        // No usable report and no snapshot: rebuild from the answers on this
        // device, or from the response log of the learner's latest completed
        // run, rather than showing an empty shell to someone who has already
        // answered every question.
        if (await backfillDnaSnapshot()) {
          const rebuilt = readLocalDna();
          if (rebuilt) return projectLocal(rebuilt);
        }
        if (sealed) return sealed;
        return preview ? project(previewStudentPayload(), true) : null;
      }
      return sealed!;
    },
  });
}

